import { DocumentVisibilityTracker, LockManager, UUIDService } from '@pulse/browser-shared';
import {
    BatchInQueue,
    PulseBatch,
    PulseBatchConfig,
    PulseMessage,
    PulseStorage,
    TrackerConfigurations,
    sleep,
} from '@pulse/common';
import { Subject } from 'rxjs';
import { PulseTransport, ResolveWith, TrackingConsentService } from '../services';
import { getBatches, getTrackerBatchConfig } from './batch-helper';

type StoreBatches = {
    batch: PulseBatch;
    batchId: string;
};

export class BatchQueue {
    private batchQueue: BatchInQueue[] = [];
    private sendingBatches: BatchInQueue[] = [];
    private trackingEnabled?: boolean;
    private currentRequestSize = 0;
    private beaconLimit = 60_000;
    private queueProcessor: ReturnType<typeof setInterval> | null = null;
    private STORED_BATECHES_KEY = 'pulse-unpublished-events';
    private batchConfig: PulseBatchConfig;
    readonly queueEmpty$ = new Subject<boolean>();

    constructor(
        private storage: PulseStorage,
        private lockManager: LockManager,
        private uuid: UUIDService,
        private visibility: DocumentVisibilityTracker,
        private consentService: TrackingConsentService,
        private transport: PulseTransport,
        private config: TrackerConfigurations,
    ) {
        if (this.trackingEnabled) {
            this.queueStoredMessagesIfAny();
        }
        this.listenAndQueueOnStoreChanges();

        this.consentService.trackingEnabled().subscribe((enabled) => {
            this.trackingEnabled = enabled;
            this.batchConfig = getTrackerBatchConfig(this.config, this.trackingEnabled);
        });

        this.batchConfig = getTrackerBatchConfig(this.config, this.trackingEnabled);
    }

    pushBatch(batch: PulseBatch) {
        const batchId = this.uuid.generate();

        if (this.visibility.visible) {
            this.batchQueue.push({ batch, batchId });
            if (!this.queueProcessor) {
                this.controlQueueProcessor();
            }
        } else {
            this.storeBatches([{ batch, batchId }]);
            this.clearQueue();
            this.sendStoredBatches();
        }
    }

    startQueue() {
        this.queueStoredMessagesIfAny();
        if (this.batchQueue.length && !this.queueProcessor) {
            this.controlQueueProcessor();
        }
    }

    getQueueEmpty$(): Subject<boolean> {
        return this.queueEmpty$;
    }

    async stopQueue() {
        this.stopQueueProcessor();
        if (this.batchQueue.length) {
            const currentBatchSize = this.batchQueue[0].batch.size || 0;

            if (
                !this.currentRequestSize ||
                this.currentRequestSize + currentBatchSize < this.beaconLimit
            ) {
                this.currentRequestSize = this.currentRequestSize + currentBatchSize;
                this.sendBatch(this.batchQueue[0].batch, this.batchQueue[0].batchId)
                    .then((res) => this.handleResponse(res))
                    .catch((err) => {
                        console.error(err);
                    });

                this.sendingBatches.push(this.batchQueue[0]);
                this.batchQueue.splice(0, 1);
            }
        }
        this.storeBatches([...this.batchQueue]);
        this.clearQueue();
        await sleep(500);
        this.sendStoredBatches();
    }

    private async queueStoredMessagesIfAny() {
        const locked = await this.lockManager.locked(this.STORED_BATECHES_KEY);

        if (locked) {
            this.postpone(this.queueStoredMessagesIfAny);

            return;
        }

        const lock = await this.lockManager.request(this.STORED_BATECHES_KEY, 6_000);

        if (!lock) {
            this.postpone(this.queueStoredMessagesIfAny);

            return;
        }

        const storedBatches =
            this.storage.getJSON<PulseMessage[] | BatchInQueue[]>(this.STORED_BATECHES_KEY) || [];

        this.storage.remove(this.STORED_BATECHES_KEY);
        this.lockManager.release(lock);
        if (!storedBatches.length) {
            return;
        }
        if ('batchId' in storedBatches[0]) {
            (storedBatches as BatchInQueue[]).forEach((batch) => {
                if (!this.batchQueue.some((queuedBatch) => queuedBatch.batchId === batch.batchId)) {
                    this.batchQueue.push(batch);
                }
            });
        } else {
            const batches = getBatches(storedBatches as PulseMessage[], this.batchConfig);
            batches.forEach((batch) => this.pushBatch(batch));
        }
        if (this.batchQueue.length && !this.queueProcessor) {
            this.controlQueueProcessor();
        }
    }

    private listenAndQueueOnStoreChanges() {
        this.storage.change(this.STORED_BATECHES_KEY).subscribe(async () => {
            this.queueStoredMessagesIfAny();
        });
    }

    private storeBatches(batches?: StoreBatches[]) {
        const alreadyStored = this.storage.getJSON<BatchInQueue[]>(this.STORED_BATECHES_KEY) || [];

        const passedBatch = batches ? batches : [];
        this.storage.set(
            this.STORED_BATECHES_KEY,
            JSON.stringify([
                ...alreadyStored,
                ...this.batchQueue,
                ...passedBatch,
                ...this.sendingBatches,
            ]),
        );
    }

    private clearQueue() {
        this.batchQueue = [];
    }

    private controlQueueProcessor() {
        if (!this.queueProcessor) {
            this.queueProcessor = setInterval(() => {
                if (!this.batchQueue.length && !this.sendingBatches.length) {
                    this.queueEmpty$.next(true);
                    this.currentRequestSize = 0;
                }
                if (this.batchQueue.length === 0 && this.queueProcessor) {
                    this.stopQueueProcessor();

                    return;
                }
                const currentBatchSize = this.batchQueue[0].batch.size || 0;

                if (
                    !this.currentRequestSize ||
                    this.currentRequestSize + currentBatchSize < this.beaconLimit
                ) {
                    this.currentRequestSize += currentBatchSize;
                    const batch = this.batchQueue.shift();

                    if (batch) {
                        this.sendingBatches.push(batch);
                        this.sendBatch(batch.batch, batch.batchId)
                            .then((res) => this.handleResponse(res))
                            .catch((err) => console.error(err));
                    }
                    if (this.batchQueue.length === 0 && this.queueProcessor) {
                        if (!this.sendingBatches.length) {
                            this.queueEmpty$.next(true);
                        }

                        this.stopQueueProcessor();
                        this.queueProcessor = null;
                    }
                }
            }, 100);
        }
    }

    private stopQueueProcessor() {
        if (this.queueProcessor) {
            clearInterval(this.queueProcessor);
            this.queueProcessor = null;
        }
    }

    private handleResponse({ size, batchId }: ResolveWith) {
        this.currentRequestSize -= size;
        const index = this.sendingBatches.findIndex((batch) => batch.batchId === batchId);

        if (index !== -1) {
            this.sendingBatches.splice(index, 1);
        }
        if (!this.sendingBatches.length && !this.batchQueue.length) {
            this.queueEmpty$.next(true);
        }
    }

    private sendBatch(batch: PulseBatch, batchId: string): Promise<ResolveWith> {
        return new Promise<ResolveWith>((resolve) => {
            const keepalive = Boolean(batch.keepAlive);
            delete batch.keepAlive;
            this.transport.send(batch, keepalive, resolve, batchId);
        });
    }

    private async sendStoredBatches() {
        await sleep(1000);
        if (this.visibility.visible) {
            return;
        }
        const locked = await this.lockManager.locked(this.STORED_BATECHES_KEY);

        if (locked) {
            this.postpone(this.sendStoredBatches);

            return;
        }

        const lock = await this.lockManager.request(this.STORED_BATECHES_KEY, 6_000);

        if (!lock) {
            this.postpone(this.sendStoredBatches);

            return;
        }

        const alreadyStored = this.storage.getJSON<BatchInQueue[]>(this.STORED_BATECHES_KEY) || [];

        if (!alreadyStored.length) {
            this.lockManager.release(lock);

            return;
        }
        const batch = alreadyStored.splice(0, 1);
        this.storage.set(this.STORED_BATECHES_KEY, JSON.stringify([...alreadyStored]));
        this.lockManager.release(lock);
        this.sendBatch(batch[0].batch, batch[0].batchId)
            .then(() => {
                if (!this.visibility.visible) {
                    this.sendStoredBatches();
                }
            })
            .catch((err) => {
                console.error(err);
            });
    }

    private postpone<T>(callbeck: (data: T | undefined) => any, timeout = 1000, passedData?: T) {
        setTimeout(() => {
            callbeck.call(this, passedData);
        }, timeout);
    }
}
